import { useEffect, useRef, useState } from "react";
import { Loader2, Music2, Pause, Play, Volume2, VolumeX } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RpgSoundtrack } from "@/audio/RpgSoundtrack";
import { NIGHT_PULSE_BPM, RPG_TRACK_TITLE } from "@/audio/nightPulse";

const PREFERENCES = "playden:rpg-music:v1";
const readPreferences = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(PREFERENCES) || "{}");
    return { volume: Number.isFinite(saved.volume) ? Math.max(0, Math.min(1, saved.volume)) : 0.18, muted: saved.muted === true };
  } catch { return { volume: 0.18, muted: false }; }
};

export const RpgMusicPlayer = () => {
  const [prefs, setPrefs] = useState(readPreferences);
  const [state, setState] = useState("paused");
  const [error, setError] = useState("");
  const player = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const hidden = () => {
      if (document.hidden) { player.current?.pause(); setState(s => s === "loading" ? s : "paused"); }
    };
    document.addEventListener("visibilitychange", hidden);
    return () => {
      mounted.current = false;
      document.removeEventListener("visibilitychange", hidden);
      player.current?.dispose(); player.current = null;
    };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(PREFERENCES, JSON.stringify(prefs)); } catch { /* private storage may be unavailable */ }
    player.current?.setVolume(prefs.volume, prefs.muted);
  }, [prefs]);

  const toggle = async () => {
    if (state === "playing") { player.current?.pause(); setState("paused"); return; }
    if (state === "loading") return;
    setError(""); setState("loading");
    try {
      if (!player.current) player.current = new RpgSoundtrack();
      const instance = player.current;
      instance.setVolume(prefs.volume, prefs.muted);
      const started = await instance.play();
      if (mounted.current && player.current === instance && started) {
        if (document.hidden) { instance.pause(); setState("paused"); }
        else setState("playing");
      }
    } catch (e) {
      if (mounted.current) { setState("paused"); setError(e.message || "Music could not start. Please try again."); }
    }
  };

  return <div data-testid="rpg-music-player" data-state={state} className="flex items-center gap-0.5 rounded-lg border border-white/15 bg-black/20">
    {error && <span data-testid="rpg-music-alert" role="alert" title={error} className="max-w-28 px-2 text-xs text-amber-200">Audio unavailable</span>}
    <button data-testid="rpg-music-toggle" onClick={toggle} disabled={state === "loading"}
      aria-label={state === "playing" ? "Pause music" : "Play music"} title={state === "playing" ? "Pause music" : `Play ${RPG_TRACK_TITLE}`}
      className="h-9 w-9 grid place-items-center rounded-l-lg text-emerald-300 hover:bg-white/10 disabled:opacity-50 transition-colors">
      {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : state === "playing" ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
    </button>
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="rpg-music-settings" aria-label="Music volume and mute" title="Music volume and mute"
          className="h-9 w-9 grid place-items-center rounded-r-lg text-white/65 hover:bg-white/10 hover:text-white transition-colors">
          {prefs.muted || prefs.volume === 0 ? <VolumeX className="h-4 w-4" /> : <Music2 className="h-4 w-4" />}
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid="rpg-music-panel" align="end" className="w-64 max-w-[calc(100vw-24px)] border-white/15 bg-[hsl(222_22%_8%)] text-white">
        <p data-testid="rpg-music-title" className="text-sm font-semibold">{RPG_TRACK_TITLE}</p>
        <p data-testid="rpg-music-track-detail" className="text-xs text-white/50 mt-1">Mellow original instrumental · {NIGHT_PULSE_BPM} BPM</p>
        <div className="flex items-center gap-3 mt-4">
          <button data-testid="rpg-music-mute" aria-label={prefs.muted ? "Unmute music" : "Mute music"} aria-pressed={prefs.muted}
            onClick={() => setPrefs(p => ({ volume: p.volume || 0.18, muted: !p.muted }))}
            className="h-9 w-9 shrink-0 grid place-items-center rounded border border-white/15 text-emerald-300 hover:bg-white/10 transition-colors">
            {prefs.muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
          <input data-testid="rpg-music-volume" aria-label="Music volume" type="range" min="0" max="100" step="1"
            value={Math.round(prefs.volume * 100)} onChange={e => setPrefs({ volume: Number(e.target.value) / 100, muted: Number(e.target.value) === 0 })}
            className="min-w-0 flex-1 accent-emerald-400" />
          <output data-testid="rpg-music-volume-value" className="w-9 text-right text-xs tabular-nums text-white/70">{Math.round(prefs.volume * 100)}%</output>
        </div>
        {error && <p data-testid="rpg-music-error" role="alert" className="text-xs text-amber-200 mt-3">{error}</p>}
      </PopoverContent>
    </Popover>
  </div>;
};